/**
 * M7 S39 part 3: the live finale ("Transit Priority") for the Logs screen (`logsHost.tsx LogsFinaleSource`). The
 * storyLink feature builds it over the story service, the comms port's node view (Voss's pointing line included,
 * `ui.ts`), the claim contact's view and the worker story view cache (`view.ts`).
 *
 * - **Commit input:** the worker's `corpFaction` and `tethysAiTetherIncome10d` (none and 0 before the first read),
 *   the player's system, and the corporation's claim ids (`['corporation']` once founded, as `paths.ts` names it).
 *   TCF fleet notes: no source anywhere carries the principal (S39d searched the worker, the fleet link and the
 *   Career: TCF fleet financing is not built), so it is 0 (no notes) unless the feature passes one; the debug API
 *   does the same (`storyPanel.ts`).
 * - **Epilogue facts:** the worker's gate stage and corporation, and (S39d) the newest resolved ban vote's ballots
 *   (`StoryView.banBallots`, its yes and no weights); unset while the vote is pending (the preview fills 0).
 * - **At the Candle (PC-7):** the player's system is the Candle's (`finale.json candleHint`, resolved by the story
 *   sites).
 *
 * Event-driven (UI actions); never per frame.
 */
import type { Career } from '../../career/types'
import type { CaseData } from '../../story/bundle'
import type { CommsView } from '../../story/comms'
import type { ContactView } from '../../story/contacts'
import type { EpilogueFacts } from '../../story/finale/epilogue'
import type { FinaleInput } from '../../story/finale/paths'
import { createFinaleLink, finaleLinkConfig, finaleOpen } from '../../story-link/finale'
import type { StoryService } from '../../story-link/service'
import type { StoryView } from '../../strategic/types'
import type { LogsFinaleSource } from '../../ui/story/finaleHost'

/** The corporation's claim ids once founded (`paths.ts`: the Guardians path makes them `harbouring`). */
export const CORP_CLAIM_IDS: readonly string[] = ['corporation']

type FinaleViewFacts = Pick<StoryView, 'corpFaction' | 'tethysAiTetherIncome10d'>

/** The commit input from the cached worker view (null before the first read: no corporation, no income). */
export function finaleInputOf(view: FinaleViewFacts | null, systemId: string, fleetNotesPrincipal = 0): FinaleInput {
  const corpFaction = view?.corpFaction ?? null
  const input: FinaleInput = {
    view: { corpFaction, tethysAiTetherIncome10d: view?.tethysAiTetherIncome10d ?? 0 },
    systemId,
    fleetNotesPrincipal,
  }
  if (corpFaction !== null) input.corpClaimIds = CORP_CLAIM_IDS
  return input
}

/** The epilogue's facts from the cached worker view (the gate as normal before the first read). */
export function epilogueFactsOf(view: Pick<StoryView, 'stage' | 'corpFaction' | 'banBallots'> | null): EpilogueFacts {
  const facts: EpilogueFacts = { stage: view?.stage ?? 'normal', corpFaction: view?.corpFaction ?? null }
  const b = view?.banBallots
  if (b !== undefined) facts.ballots = { for: b.for, against: b.against }
  return facts
}

export interface LiveFinaleDeps {
  service: Pick<StoryService, 'apply' | 'resolveSystem'>
  career: () => Career | null
  /** The comms port's node view (`ui.comms.view`). */
  node: (nodeId: string) => CommsView | null
  contactView: () => ContactView
  /** The worker story view cache's last read. */
  view: () => StoryView | null
  /** The player's system id ('' when unknown). */
  systemId: () => string
  /** TCF fleet-notes principal outstanding (absent: 0, see the header). */
  fleetNotesPrincipal?: () => number
  caseData: () => CaseData
}

export function createLiveFinale(deps: LiveFinaleDeps): LogsFinaleSource {
  const candleHint = finaleLinkConfig().candleHint
  const source = createFinaleLink({
    service: deps.service,
    career: deps.career,
    node: deps.node,
    contactView: deps.contactView,
    input: () => finaleInputOf(deps.view(), deps.systemId(), deps.fleetNotesPrincipal?.() ?? 0),
    facts: () => epilogueFactsOf(deps.view()),
    atCandle: () => {
      const here = deps.systemId()
      return here !== '' && deps.service.resolveSystem(candleHint) === here
    },
  })
  return {
    source,
    open: () => {
      const c = deps.career()
      return c !== null && finaleOpen(c)
    },
    caseData: deps.caseData,
  }
}
